import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Play, Pause } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface AudioRecorderProps {
  referenceTrackUrl?: string;
  maxDurationSeconds?: number;
  onRecordingComplete: (blob: Blob, durationSeconds: number) => void;
  disabled?: boolean;
}

type RecorderState = "idle" | "counting" | "recording" | "done";

export const AudioRecorder = ({
  referenceTrackUrl,
  maxDurationSeconds = 45,
  onRecordingComplete,
  disabled = false,
}: AudioRecorderProps) => {
  const [state, setState] = useState<RecorderState>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [countIn, setCountIn] = useState(0); // 4,3,2,1
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [refReady, setRefReady] = useState(!referenceTrackUrl);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const refAudioRef = useRef<HTMLAudioElement | null>(null);
  const playbackRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countInTimerRef = useRef<number | null>(null);
  const elapsedRef = useRef(0);

  // Load reference track
  useEffect(() => {
    if (referenceTrackUrl) {
      const audio = new Audio(referenceTrackUrl);
      audio.loop = true;
      audio.preload = "auto";
      audio.addEventListener("canplaythrough", () => setRefReady(true));
      refAudioRef.current = audio;
      return () => { audio.pause(); audio.src = ""; };
    }
  }, [referenceTrackUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countInTimerRef.current) clearInterval(countInTimerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (playbackUrl) URL.revokeObjectURL(playbackUrl);
    };
  }, [playbackUrl]);

  // Play a click sound for count-in beats
  const playClick = useCallback((beat: number) => {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = beat === 1 ? 1200 : 800; // higher pitch on beat 1
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
      setTimeout(() => ctx.close(), 200);
    } catch {}
  }, []);

  const beginRecording = useCallback(async (stream: MediaStream) => {
    const recorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm",
    });
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      stream.getTracks().forEach(t => t.stop());
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const url = URL.createObjectURL(blob);
      setPlaybackUrl(url);
      setState("done");
      onRecordingComplete(blob, elapsedRef.current);
      if (refAudioRef.current) {
        refAudioRef.current.pause();
        refAudioRef.current.currentTime = 0;
      }
    };

    // Start reference track now (recording is already capturing)
    if (refAudioRef.current) {
      refAudioRef.current.currentTime = 0;
      refAudioRef.current.play().catch(() => {});
    }

    recorder.start(100);
    setState("recording");
    setElapsed(0);
    elapsedRef.current = 0;

    timerRef.current = window.setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1;
        elapsedRef.current = next;
        if (next >= maxDurationSeconds) {
          recorder.stop();
          if (timerRef.current) clearInterval(timerRef.current);
        }
        return next;
      });
    }, 1000);
  }, [maxDurationSeconds, onRecordingComplete]);

  const startWithCountIn = useCallback(async () => {
    try {
      // Get mic access immediately on user gesture
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      // Start 4-beat count-in (1 second per beat)
      setState("counting");
      let beat = 4;
      setCountIn(beat);
      playClick(beat);

      countInTimerRef.current = window.setInterval(() => {
        beat -= 1;
        if (beat <= 0) {
          if (countInTimerRef.current) clearInterval(countInTimerRef.current);
          setCountIn(0);
          beginRecording(stream);
        } else {
          setCountIn(beat);
          playClick(beat);
        }
      }, 750); // ~80 BPM count-in tempo

    } catch (err) {
      console.error("Microphone access denied:", err);
    }
  }, [beginRecording, playClick]);

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const togglePlayback = () => {
    if (!playbackRef.current) return;
    if (isPlayingBack) {
      playbackRef.current.pause();
    } else {
      playbackRef.current.play();
    }
    setIsPlayingBack(!isPlayingBack);
  };

  const progressPercent = (elapsed / maxDurationSeconds) * 100;

  return (
    <div className="space-y-4 p-4 rounded-xl border bg-card">
      {/* Reference track note */}
      {referenceTrackUrl && state === "idle" && (
        <p className="text-xs text-muted-foreground text-center">
          {refReady ? "🎧 Use headphones — reference track plays during recording" : "Loading reference track..."}
        </p>
      )}

      {/* Count-in display */}
      {state === "counting" && (
        <div className="text-center py-4">
          <p className="text-6xl font-mono font-black text-primary animate-pulse tabular-nums">
            {countIn}
          </p>
          <p className="text-sm text-muted-foreground mt-2">Get ready...</p>
        </div>
      )}

      {/* Timer display */}
      {state !== "counting" && (
        <div className="text-center">
          <p className="text-4xl font-mono font-bold tabular-nums">
            {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {state === "recording" ? `Max ${maxDurationSeconds}s` : state === "done" ? "Recording complete" : "Ready to record"}
          </p>
        </div>
      )}

      {/* Progress bar during recording */}
      {state === "recording" && (
        <Progress value={progressPercent} className="h-2" />
      )}

      {/* Controls */}
      <div className="flex justify-center gap-3">
        {state === "idle" && (
          <Button
            onClick={startWithCountIn}
            disabled={disabled || !refReady}
            size="lg"
            className="gap-2"
          >
            <Mic className="h-5 w-5" />
            Start Recording
          </Button>
        )}

        {state === "recording" && (
          <Button
            onClick={stopRecording}
            variant="destructive"
            size="lg"
            className="gap-2"
            disabled={elapsed < 5}
          >
            <Square className="h-5 w-5" />
            Stop ({maxDurationSeconds - elapsed}s left)
          </Button>
        )}

        {state === "done" && playbackUrl && (
          <>
            <audio
              ref={playbackRef}
              src={playbackUrl}
              onEnded={() => setIsPlayingBack(false)}
            />
            <Button onClick={togglePlayback} variant="outline" size="lg" className="gap-2">
              {isPlayingBack ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              {isPlayingBack ? "Pause" : "Review"}
            </Button>
          </>
        )}
      </div>

      {/* Notices */}
      {state === "idle" && (
        <p className="text-xs text-muted-foreground text-center">
          ⚡ Single take only — 4-beat count-in before recording
        </p>
      )}
      {state === "done" && (
        <p className="text-xs text-primary text-center font-medium">
          ✓ Recording saved. You may continue.
        </p>
      )}
    </div>
  );
};
